import { Grid, CircularProgress, InputAdornment } from "@mui/material";
import { Close as CloseIcon, Done as DoneIcon } from '@mui/icons-material';
import React, { useEffect, useState } from "react";
import { CheckEmailStyle, StyledErrorMessage } from "./checkEmail.style"
import {StyledAsterisk, StyledHelperText} from "../commonComponents/Textbox/Textbox.style";
import TextBox from "../commonComponents/Textbox/index";
import { useTheme } from "styled-components";
import Box from "component/layoutComponents/Box";

export interface ICheckEmailProps {
  handleCheckEmail: (arg0: any) => void;
  handleEmailChange : (arg0: any) => void;
  label: string;
  isCheckUserOnly: boolean;
  loadingCheckEmail: boolean;
  errors: any;
  isOpenManuallyInput: boolean;
  title: string;
  isEditable: boolean;
  data: any | null;
  locale: {
    label: string;
    labelManager: string;
    placeholder: string;
    searchLabel: string;
    error_link: string;
    emailDialog: {
      title: string,
      contentTitle: string,
      emailAddress: string,
      emailError: string,
      firstName: string,
      lastName: string,
      workType: string,
      contractor: string,
      employee: string,
      btnCancel: string,
      btnContinue: string,
    },
    textboxToolTip: string;
  },
  isShowLabel?: boolean | true,
  placeholder?: string,
  isManagerEmail?: boolean | false,
  disableShadow?: boolean,
  isSpecialCharacterAllowed?: boolean;
  queryUserByUserName?: boolean;
  emailPopulated?: string;
  commonStyle?: any;
  requiredField?: boolean;
  inputId?: string;
  disabled?: boolean;
  disabledBackground?: boolean;
  helperText?: string;
}

const CheckEmail = (props: ICheckEmailProps) => {

  const {
    label,
    handleCheckEmail,
    handleEmailChange,
    data,
    loadingCheckEmail,
    errors,
    isEditable,
    isCheckUserOnly,
    isOpenManuallyInput,
    locale,
    placeholder,
    disableShadow,
    isSpecialCharacterAllowed,
    queryUserByUserName,
    emailPopulated,
    requiredField = true,
    commonStyle,
    disabled,
    disabledBackground,
    helperText,
    inputId = "txt-checkemail-email"
  } = props;

  let hasData = !!data;

  const [valueEmail, setValueEmail] = useState(() => {
    if (!hasData) return '';
    if (queryUserByUserName) return data.user_name || data.email;
    return data.email;
  });
  const [differentValue, setDifferentValue] = useState(true);

  const theme = useTheme();

  const hasError = !loadingCheckEmail && errors?.length > 0 && !data;

  useEffect(() => {
    setValueEmail(emailPopulated);
  }, [emailPopulated]);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBlur = () => {
    if (differentValue && valueEmail !== data?.email) {
      handleSearch();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setDifferentValue(valueEmail !== newValue);
    setValueEmail(newValue);
    handleEmailChange(newValue);
  };

  const handleSearch = () => {
    if (!!valueEmail && !loadingCheckEmail) {
      handleCheckEmail(valueEmail);
      setDifferentValue(false);
    }
  };

  const renderEndAdornment = () => {
    if (disabled) return <></>;
    if (loadingCheckEmail) return <CircularProgress size={24} />;
    if (hasError) return <CloseIcon sx={{ color: theme.uptimeColors.generalColors.state.danger.light }} />;
    const isValidEmail = hasData && (!differentValue || valueEmail === (queryUserByUserName ? data.user_name : data.email));
    if (isValidEmail) return <DoneIcon sx={{ color: theme.uptimeColors.generalColors.state.success.light }} />;
    return <></>;
  };

  return (
    <CheckEmailStyle disableShadow={disableShadow} commonStyle={commonStyle}>
        {!!props.title && <h5 className="check-mail-title">{props.title}</h5>}
        <Grid container className={isCheckUserOnly? "section-email section-email-check-user":"section-email"}>
          {isEditable && <>
            {props.isShowLabel && (
              <Grid xs={12} item>
                <Box>
                  <label className="label-form">
                    {props.isManagerEmail ? locale?.labelManager : label || locale?.label}
                  </label>
                  {requiredField && <StyledAsterisk>*</StyledAsterisk>}
                  {helperText && <StyledHelperText>{helperText}</StyledHelperText>}
                </Box>
              </Grid>
            )}

            <Grid container spacing={commonStyle?.spacing ?? 1} className="email-container">
              <Grid item xs={12} className="email-control">
                <TextBox
                  className='email-input'
                  type="text"
                  onKeyUp={handleKeyUp}
                  onBlur={handleBlur}
                  placeholder={!!placeholder ? placeholder : locale?.placeholder}
                  name='email'
                  value={valueEmail}
                  onChange={(e) => handleChange(e)}
                  fullWidth
                  isSpecialCharacterAllowed={isSpecialCharacterAllowed}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{renderEndAdornment()}</InputAdornment>,
                  }}
                  required
                  textboxToolTip={locale?.textboxToolTip}
                  id={inputId}
                  disabled={disabled}
                  disabledBackground={disabledBackground}
                />
              </Grid>
            </Grid>
          </>}
          <Grid>
            {hasError &&
              <>
                <StyledErrorMessage id="lbl-checkemail-error">{errors[0]}</StyledErrorMessage>
                {!isCheckUserOnly && isOpenManuallyInput &&<p className="hint-msg" id="lbl-checkemail-hint_msg">{errors[1]}</p>}
              </>
            }
          </Grid>
        </Grid>

    </CheckEmailStyle>
  );
};

export const defaultProps = {
  handleCheckEmail: () => {},
  handleEmailChange: () => {},
  label: "",
  isCheckUserOnly: false,
  loadingCheckEmail: false,
  errors: [],
  isOpenManuallyInput: true,
  title: "",
  isEditable: true,
  data: null,
  locale: {
    title: "",
    label: "Please enter recipient email",
    labelManager: "Recipient's Direct Manager Email",
    placeholder: "Enter an email",
    searchLabel: "Search",
    error_1: "For new joiners,",
    error_link: "Click Here",
    error_2: "to manually enter their information.",
    errorInvalid: "Email is invalid.",
    errorUser: "Can't find user",
    VALIDATION_MESSAGES: {
      INFORMATION_TEXT: "For new hires, {Click_Here} to manually enter their information.",
      INVALID_EMAIL_DOMAIN: "Please enter a valid email address containing {email_domain}",
      USER_NOT_FOUND: "Sorry, This email is not activated yet or does not exist."
    },
    emailDialog: {
      title: "Recipient Information",
      label: "Please enter Hiring Manager work email",
      contentTitle: "We will temporarily assign the device to you, the requestor.<br/> Once the PC recipient logs in with the new device, they will become the registered user.",
      emailAddress: "Email Address",
      emailError: "It should be the same with previous search page.",
      firstName: "First Name",
      lastName: "Last Name",
      workType: "Worker Type",
      contractor: "Contractor",
      employee: "Employee",
      btnCancel: "CANCEL",
      btnContinue: "CONTINUE"
    },
    textboxToolTip: "Please fill out this field."
  },
  isShowLabel: true,
  disableShadow: false,
  emailPopulated: "",
  commonStyle: {},
  requiredField: true,
  inputId: "txt-checkemail-email"
};
CheckEmail.defaultProps = defaultProps;

export default CheckEmail;